import {
    RouterProvider,
    Outlet,
    createHashRouter,
    useLocation
} from 'react-router-dom';
import './App.css';

import Generate from "./containers/Generate";
import React, { useEffect, useState } from "react";
import NavigationMenu from "./components/NavigationMenu";
import MyCollection from "./containers/MyCollection";
import Settings from "./containers/Settings";
import Templates from "./containers/Templates";
import {GenerateProvider} from "./context/GenerateContext";
import {PromptusAPIProvider} from "./context/PromptusAPIContext";
import {db, initRecommendedSettings, updateRecommendedSettings} from "./db";
import {getApiBaseUrl, getAuthorizationToken} from "./api-helper";
import { exportDB } from "dexie-export-import";
import Profile from "./containers/Profile";
import Explore from "./containers/Explore";
import HowTo from "./containers/HowTo";
import Chat from "./containers/Chat";
import ChatSafe from "./containers/ChatSafe";
import ChatDebug from "./containers/ChatDebug";
import ChatEmpty from "./containers/ChatEmpty";
import { DriverProvider } from './context/DriverContext';
import { CollectionProvider } from './context/CollectionContext';
import { AccountProvider } from './context/AccountContext';
import Marketplace from './containers/Marketplace';
import { isUndefined } from 'cropperjs';
import { PromptProvider } from './context/PromptContext';
import ComfyUI from './containers/ComfyUI';
import Output from './containers/ComfyOutput';
import CosyflowEditor from './containers/CosyflowEditor';
import ErrorBoundary from './components/ErrorBoundary';
import TestLoginModal from './components/TestLoginModal';
import {getRecommendedSettings} from "./api-helper";
import { MenuProvider } from './context/MenuContext';
import { MommProvider } from './context/MommContext';
import ZoomIndicator from "./components/ZoomIndicator";
import ZoomAwareCSS from "./components/ZoomAwareCSS";
import PopupImage from './images/Promptus_tutorial.webp';
import Logo from "./images/logo_dark_blue.png";
import electronBridge from './utils/electronBridge';
import GreyLogo from "./images/logo_light_grey.png";
import eventBus from './utils/eventBus.js';
import DpiScaled from "./components/DpiScaled.jsx";
import { updateAllModels } from "./updateAllModels";
import ContentOverlay from "./components/ContentOverlay";
import { useInternetBypass } from './hooks/useInternetConnectivity';
import CosyUI from './containers/CosyUI.jsx';
import PaymentStatus from './containers/PaymentStatus.jsx';

// SQLite migration utility - removed to prevent browser errors
// import { initMigration } from './utils/migrateToSQLite';

// Lazy load MOMM components to prevent SQLite errors in browser
const MOMM = React.lazy(() => import("./containers/MOMM"));
const MommDemo = React.lazy(() => import("./containers/MommDemo"));

const MainLayout = () => {
    const location = useLocation();
    const hideButtonOn = ["/"]; // Add all routes where button should be hidden

    return (
        <>
            {electronBridge.isElectron ?
                <div className="flex min-h-0 flex-col md:flex-row w-full h-full text-white zoom-aware-container">
                <GenerateProvider>
                    <PromptusAPIProvider>
                        <DriverProvider>
                            <CollectionProvider>
                                <AccountProvider>
                                    <PromptProvider>
                                        <MenuProvider>
                                            <MommProvider>
                                                <ErrorBoundary>
                                                    <ZoomAwareCSS />
                                                    <NavigationMenu />
                                                    <main className="relative flex-grow min-h-0 w-full h-full overflow-auto main-content responsive-content px-4">
                                                        <Outlet />
                                                        <ContentOverlay />
                                                    </main>

                                                    {/* Global button — hidden on certain pages */}
                                                    {!hideButtonOn.includes(location.pathname) && (
                                                        <button className="absolute top-3 left-3 border-2 border-cyan/50 bg-dark-blue rounded-lg min-middle:hidden max-lg-inclusive:col-span-1 p-1 cursor-pointer w-12" onClick={() => eventBus.emit('show_nav_menu')}><img src={GreyLogo} className="mx-auto text-black"/></button>
                                                    )}
                                                    <ZoomIndicator />
                                                </ErrorBoundary>
                                            </MommProvider>
                                        </MenuProvider>
                                    </PromptProvider>
                                </AccountProvider>
                            </CollectionProvider>    
                        </DriverProvider>
                    </PromptusAPIProvider>
                </GenerateProvider>
                </div>
            :
                <DpiScaled baselineDpr={1}>
                    <div className="flex min-h-0 flex-col md:flex-row w-full h-full text-white">
                        <GenerateProvider>
                            <PromptusAPIProvider>
                                <DriverProvider>
                                    <CollectionProvider>
                                        <AccountProvider>
                                            <PromptProvider>
                                                <MenuProvider>
                                                    <MommProvider>
                                                        <ErrorBoundary>
                                                            <ZoomAwareCSS />
                                                            <NavigationMenu />
                                                            <main className="relative flex-grow min-h-0 w-full min-w-0 h-full overflow-auto main-content responsive-content px-4">
                                                                <Outlet />
                                                                <ContentOverlay />
                                                            </main>

                                                            {/* Global button — hidden on certain pages */}
                                                            {!hideButtonOn.includes(location.pathname) && (
                                                                <button className="absolute top-3 left-3 border-2 border-cyan/50 bg-dark-blue rounded-lg min-middle:hidden max-lg-inclusive:col-span-1 p-1 cursor-pointer w-12" onClick={() => eventBus.emit('show_nav_menu')}><img src={GreyLogo} className="mx-auto text-black"/></button>
                                                            )}
                                                            <ZoomIndicator />
                                                        </ErrorBoundary>
                                                    </MommProvider>
                                                </MenuProvider>
                                            </PromptProvider>
                                        </AccountProvider>
                                    </CollectionProvider>    
                                </DriverProvider>
                            </PromptusAPIProvider>
                        </GenerateProvider>
                    </div>
                </DpiScaled>

            }
        </>
    );
};

// Create routes array conditionally based on environment
const routes = [
    {
        path: "/",
        element: <ErrorBoundary><Generate /></ErrorBoundary>,
    },
    {
        path: "/comfy",
        element: <ErrorBoundary><Output /></ErrorBoundary>,
    },
    {
        path: "/my-collection",
        element: <ErrorBoundary><MyCollection /></ErrorBoundary>
    },
    {
        path: "/templates",
        element: <ErrorBoundary><Templates /></ErrorBoundary>
    },
    {
        path: "/templates/:templateId",
        element: <ErrorBoundary><Templates /></ErrorBoundary>
    },
    {
        path: "/settings",
        element: <ErrorBoundary><Settings /></ErrorBoundary>
    },
    {
        path: "/profile",
        element: <ErrorBoundary><Profile /></ErrorBoundary>
    },
    {
        path: "/explore",
        element: <ErrorBoundary><Explore /></ErrorBoundary>
    },
    {
        path: "/how-to",
        element: <ErrorBoundary><HowTo /></ErrorBoundary>
    },
    {
        path: "/marketplace",
        element: <ErrorBoundary><Marketplace /></ErrorBoundary>
    },
    {
        path: "/chat",
        element: <ErrorBoundary><Chat /></ErrorBoundary>
    },
    {
        path: "/comfyui",
        element: <ErrorBoundary><ComfyUI/></ErrorBoundary>
    },
    {
        path: "/cosyflow-editor",
        element: <ErrorBoundary><CosyflowEditor /></ErrorBoundary>
    },
    {
        path: "/login",
        element: <ErrorBoundary><Profile /></ErrorBoundary>,
    },
    {
        path: "/momm",
        element: <ErrorBoundary>
            <React.Suspense fallback={<div className="flex items-center justify-center h-full">Loading MOMM...</div>}>
                <MOMM />
            </React.Suspense>
        </ErrorBoundary>
    },
    {
        path: "/test-login",
        element: <ErrorBoundary><TestLoginModal /></ErrorBoundary>
    },
    {
        path: "/cosyui",
        element: <ErrorBoundary><CosyUI /></ErrorBoundary>
    },
    {
        path: "/payment-status",
        element: <ErrorBoundary><PaymentStatus /></ErrorBoundary>
    }
];

// Only add MOMM-optimized route if we're in Electron environment
if (electronBridge.isElectron) {
    routes.push(
        {
            path: "/momm-optimized",
            element: <ErrorBoundary>
                <React.Suspense fallback={<div className="flex items-center justify-center h-full">Loading MOMM Demo...</div>}>
                    <MommDemo />
                </React.Suspense>
            </ErrorBoundary>
        }
    );
}

const router = createHashRouter([
    {
        element: <MainLayout/>,
        errorElement: <ErrorBoundary />,
        children: routes
    }
]);

// Add the defaultRecommendedSettings data
const defaultRecommendedSettings = [
    { "model": "(SH) Stable Cascade 2.0", "steps": "12", "guidance": "7.5", "imageCount": "1", "imageWidth": "1024", "imageHeight": "1024", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "sd_xl_turbo_1.0.safetensors", "steps": "2", "guidance": "0", "imageCount": "1", "imageWidth": "512", "imageHeight": "512", "scheduler": "LCM", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "(SH) AlbedoBase XL", "steps": "20", "guidance": "7.5", "imageCount": "1", "imageWidth": "512", "imageHeight": "512", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "(SH) Animagine XL", "steps": "20", "guidance": "7.5", "imageCount": "1", "imageWidth": "1024", "imageHeight": "1024", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "(SH) Anime Illust Diffusion XL", "steps": "20", "guidance": "7.5", "imageCount": "1", "imageWidth": "1024", "imageHeight": "1024", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "(SH) DreamShaper XL", "steps": "20", "guidance": "7.5", "imageCount": "1", "imageWidth": "1024", "imageHeight": "1024", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "(SH) ICBINP XL", "steps": "20", "guidance": "7.5", "imageCount": "1", "imageWidth": "1024", "imageHeight": "1024", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "(SH) Juggernaut XL", "steps": "20", "guidance": "7.5", "imageCount": "1", "imageWidth": "1024", "imageHeight": "1024", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "(SH) Pony Diffusion XL", "steps": "20", "guidance": "7.5", "imageCount": "1", "imageWidth": "1024", "imageHeight": "1024", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "sd_xl_base_1.0.safetensors", "steps": "20", "guidance": "7.5", "imageCount": "1", "imageWidth": "1024", "imageHeight": "1024", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
    { "model": "sd3_medium_incl_clips.safetensors", "steps": "30", "guidance": "7.5", "imageCount": "1", "imageWidth": "1024", "imageHeight": "1024", "scheduler": "DPM-Solver", "tile": "0", "shuffle": "0", "upscale": "0" },
];

function App() {
            // Add this state for the popup
            const [showPopup, setShowPopup] = useState(() => {
                // Optionally, check localStorage to persist choice
                return !localStorage.getItem('firstTimeUser');
            });

            // Add internet connectivity check for offline protection
            const { shouldBypass } = useInternetBypass();
    
            const API_KEY = "71febdb733c96a2b3361415f72d1401b0bab59b0a4399dc411dc21c69e1747ab"
            const DEMO_ID = "cmca6n0dy1fcp8qszi4nervvg"
        
            function runTour() {
                // 1 ⟶ launch the demo
                window.Supademo(API_KEY).loadDemo(DEMO_ID);
              
                // 2 ⟶ grab (or wait for) the wrapper
                const poll = setInterval(() => {
                  const wrapper = document.getElementById("supademo-modal-container");
                  if (!wrapper) return;               // keep polling until SDK inserts it
                  clearInterval(poll);
              
                  // 3 ⟶ observe its child list
                  const observer = new MutationObserver(() => {
                    if (wrapper.childElementCount === 0) {
                      // ✅ demo fully closed
                      localStorage.setItem("firstTimeUser", "true");
                      setShowPopup(false);
                      observer.disconnect();
                    }
                  });
              
                  observer.observe(wrapper, { childList: true });
                }, 150); // poll every 150 ms until wrapper exists
              }
        
            // Handler for button click
            const handlePopupChoice = (choice) => {
                // Do something with the choice if needed
                if (choice == "tutorial") {
                    runTour();
                } else {
                    localStorage.setItem('firstTimeUser', 'true');
                    setShowPopup(false);
                }
            };
        
            // The popup JSX
            const popup = (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white">
                <div className="w-full h-full flex shadow-2xl overflow-hidden">
                  {/* Left: Image */}
                  <div className="hidden mobile:block mobile:w-3/5 bg-gray-900">
                    <img
                      src={PopupImage} // <-- update this path to your image
                      alt="Welcome"
                      className="object-cover w-full h-full"
                    />
                  </div>
                  {/* Right: Content */}
                  <div className="w-full mobile:w-2/5 flex flex-col justify-center px-8 py-10 bg-white">
                    <div className="mb-4">
                      <div className="text-4xl tablet:text-6xl font-bold text-dark-blue">Welcome to</div>
                      <div className="flex items-center mt-1">
                        <img
                          src={Logo} // <-- update this path to your logo if needed
                          alt="Promptus"
                          className="h-18 tablet:h-24 mr-2"
                        />
                        <span className="text-4xl tablet:text-6xl font-bold text-dark-blue">Promptus</span>
                      </div>
                    </div>
                    <div className="font-semibold text-xl text-dark-blue mb-2">
                      Ready to become a pro?
                    </div>
                    <div className="text-gray-700 text-lg mb-8">
                      Most successful users start here. Our 2-minute tutorial shows you exactly how to get results fast.
                    </div>
                    <button
                      onClick={() => handlePopupChoice("tutorial")}
                      className="w-full text-lg bg-dark-blue text-white font-semibold py-3 rounded-lg mb-3 shadow hover:bg-blue-900 transition"
                    >
                      Yes, show me how
                    </button>
                    <button
                      onClick={() => handlePopupChoice("skip")}
                      className="w-full text-lg text-dark-blue font-medium underline hover:text-blue-900 transition"
                    >
                      Skip for now
                    </button>
                  </div>
                </div>
              </div>
            );
    


    const ipcRenderer = electronBridge.ipcRenderer;

    electronBridge.log("React - App Starting")
    
    // Add deep link handler
    useEffect(() => {
        // Only set up deep link handler in Electron environment
        if (!electronBridge.isElectron) {
            return;
        }
        
        const handleModelDeeplink = (event, data) => {
            console.log('Model deeplink received in React:', data);
            console.log('Model name:', data.modelName);
            console.log('Full URL:', data.fullUrl);
            
            // Send log to Electron main process console
            electronBridge.log(`Model deeplink received: ${data.modelName}`);
            
            // Navigate to Generate page with the model (root path)
            window.location.hash = '#/';
            
            // Store the model in sessionStorage to pass to Generate component
            sessionStorage.setItem('deepLinkModel', data.modelName);
            
            // Trigger a custom event to notify the Generate component
            window.dispatchEvent(new CustomEvent('modelDeepLink', { 
                detail: { modelName: data.modelName } 
            }));
        };
        
        ipcRenderer.on('model-deeplink', handleModelDeeplink);
        
        return () => {
            ipcRenderer.off('model-deeplink', handleModelDeeplink);
        };
    }, [ipcRenderer]);
    
    // Add global error handlers to prevent app crashes
    useEffect(() => {
        // Handler for uncaught exceptions
        const handleGlobalError = (event) => {
            console.error("Global error caught:", event.error || event.message);
            // Store error info for debugging
            try {
                localStorage.setItem('global_error', JSON.stringify({
                    timestamp: Date.now(),
                    message: event.error?.message || event.message || "Unknown error",
                    stack: event.error?.stack || "No stack trace available"
                }));
            } catch (e) {
                console.error("Failed to store error in localStorage:", e);
            }
            
            // Prevent app from crashing with a white screen
            event.preventDefault();
        };
        
        // Handler for unhandled promise rejections
        const handleUnhandledRejection = (event) => {
            console.error("Unhandled promise rejection:", event.reason);
            // Store rejection info for debugging
            try {
                localStorage.setItem('unhandled_rejection', JSON.stringify({
                    timestamp: Date.now(),
                    message: event.reason?.message || "Unknown rejection reason",
                    stack: event.reason?.stack || "No stack trace available"
                }));
            } catch (e) {
                console.error("Failed to store rejection in localStorage:", e);
            }
            
            // Prevent app from crashing with a white screen
            event.preventDefault();
        };
        
        // Add event listeners
        window.addEventListener('error', handleGlobalError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        
        // Handle localStorage recovery for app restoration after a crash
        const checkForRecoveryData = () => {
            try {
                const crashRecovery = localStorage.getItem('crash_recovery');
                if (crashRecovery) {
                    console.log("Found crash recovery data, attempting to restore state");
                    // Clear the recovery data to prevent infinite loop
                    localStorage.removeItem('crash_recovery');
                }
                
                // Add failsafe timer to clear emergency data after 5 minutes
                // This prevents stale data from persisting after restarts
                setTimeout(() => {
                    localStorage.removeItem('emergency_fallback_image');
                    localStorage.removeItem('image_transfer_active');
                    localStorage.removeItem('temp_image_data');
                }, 300000); // 5 minutes
            } catch (e) {
                console.error("Error checking for recovery data:", e);
            }
        };
        
        checkForRecoveryData();
        
        // Cleanup function
        return () => {
            window.removeEventListener('error', handleGlobalError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);
    
    // Initialize app settings
    useEffect(() => {
        // Helper function to safely initialize a setting
        const initSetting = async (key, defaultValue) => {
            try {
                const setting = await db.settings.where('key').equals(key).first();
                if (!setting) {
                    console.log(`Initializing setting: ${key} with value: ${defaultValue}`);
                    await db.settings.put({key: key, value: defaultValue});
                }
            } catch (error) {
                console.error(`Error initializing setting: ${key}`, error);
                // Attempt to recover by setting the default value
                try {
                    await db.settings.put({key: key, value: defaultValue});
                } catch (putError) {
                    console.error(`Fatal error initializing setting: ${key}`, putError);
                }
            }
        };

        // Initialize all required settings
        const initializeSettings = async () => {
            await initSetting('low_memory', true);
            await initSetting('safe_mode', true);
            await initSetting('test_mode', false);
            await initSetting('play_sounds', true);
            await initSetting('full_precision', false);
            await initSetting('half_model', false);
            await initSetting('cpu_generation', false);
            await initSetting('cpu_offloading', false);
            await initSetting('category_popup', false);
            await initSetting('local_directory', null);
            await initSetting('use_dev_api', false);
            await initSetting('enable_animations', true);
            await initSetting('enable_auto_sound', true);
            await initSetting('api_host', `http://${process.env.REACT_APP_PROMPTUS_LOCALHOST || 'localhost:5000'}`);
            await initSetting('local_gpu', "http://localhost:7111/")
            await initSetting('local_cpu', "http://localhost:7112/")
            await initSetting('local_cosy', "http://localhost:8190/")
        };

        initializeSettings();

        // Make the localStorage handling more robust against null/invalid values
        try {
            const workloadData = localStorage.getItem('remember_workload');
            if (!workloadData) {
                // If no data exists, set default values
                localStorage.setItem('remember_workload', JSON.stringify({
                    active: false,
                    uid: "",
                    count: 0,
                    totalImage: 0,
                    num: 0,
                }));
            } else {
                // Make sure existing data is valid JSON
                try {
                    JSON.parse(workloadData);
                } catch (parseError) {
                    // If parse fails, reset to default values
                    localStorage.setItem('remember_workload', JSON.stringify({
                        active: false,
                        uid: "",
                        count: 0,
                        totalImage: 0,
                        num: 0,
                    }));
                }
            }
        } catch (error) {
            console.error("Error handling remember_workload localStorage:", error);
        }
    }, []);
    
    // Initialize recommended settings at app startup
    useEffect(() => {
        const setupRecommendedSettings = async () => {
            try {
                // First initialize with default settings
                await initRecommendedSettings(defaultRecommendedSettings);
                
                // Then try to update from API - only if online
                if (!shouldBypass) {
                    try {
                        const apiSettings = await getRecommendedSettings();
                        if (apiSettings) {
                            await updateRecommendedSettings(apiSettings);
                        }
                    } catch (apiError) {
                        console.log("Could not fetch settings from API:", apiError);
                    }
                } else {
                    console.log("Offline mode - skipping recommended settings API fetch");
                }
                
                // Fetch and store cosyflows data once on app load - only if online
                if (!shouldBypass) {
                    console.log("[Cosyflows] === STARTING COSYFLOWS SYNC CHECK ===");
                    try {
                    // Check if cosyflows data already exists in IndexedDB
                    const existingCosyflows = await db.cosyflows.toArray();
                    const existingCount = existingCosyflows?.length || 0;
                    console.log(`[Cosyflows] Found ${existingCount} existing cosyflows in IndexedDB`);
                    
                    // Only fetch if the table is empty
                    if (existingCount === 0) {
                        console.log("[Cosyflows] Table is empty, fetching all cosyflows from API...");
                        
                        // Import axios since it's used in PromptHeader
                        const axios = (await import('axios')).default;
                        
                        // Fetch all cosyflows from API (without query params)
                        const apiBase = await getApiBaseUrl();
                        const response = await axios.get(`${apiBase}/api/cosyflows/query`, {
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${getAuthorizationToken()}`
                            }
                        });
                        
                        console.log("[Cosyflows] API Response structure:", {
                            hasData: !!response.data,
                            hasCosyflows: !!response.data?.cosyflows,
                            isArray: Array.isArray(response.data?.cosyflows),
                            responseKeys: response.data ? Object.keys(response.data) : 'No data'
                        });
                        
                        if (response.data && response.data.cosyflows) {
                            console.log(`[Cosyflows] API returned ${response.data.cosyflows?.length || 0} cosyflows`);
                            console.log("[Cosyflows] Processing cosyflows:", response.data.cosyflows);
                            
                            // Store each cosyflow as a separate row in IndexedDB
                            let successCount = 0;
                            let failedItems = [];
                            
                            for (const item of response.data.cosyflows) {
                                try {
                                    // Parse the cosyflow JSON string
                                    const cosyflowData = JSON.parse(item.cosyflow);
                                    
                                    console.log(`[Cosyflows] Successfully parsed cosyflow: ${cosyflowData.title || 'Unknown'}`);
                                    
                                    // Check if this cosyflow already exists
                                    // Can't use indexed query on nested data, so use filter instead
                                    const allCosyflows = await db.cosyflows.toArray();
                                    const existing = allCosyflows.find(cf => cf.data && cf.data.title === cosyflowData.title);
                                    if (existing) {
                                        console.log(`[Cosyflows] Cosyflow ${cosyflowData.title} already exists, updating...`);
                                        await db.cosyflows.update(existing.id, {
                                            data: cosyflowData,
                                            timestamp: Date.now()
                                        });
                                    } else {
                                        // Store as individual row with timestamp
                                        await db.cosyflows.add({
                                            data: cosyflowData,
                                            timestamp: Date.now()
                                        });
                                    }
                                    successCount++;
                                    console.log(`[Cosyflows] Stored ${cosyflowData.title || 'Unknown'} successfully`);
                                } catch (error) {
                                    console.error(`[Cosyflows] Error parsing/storing cosyflow ${item.title || 'Unknown'}:`, error);
                                    console.error("[Cosyflows] Failed item:", item);
                                    failedItems.push({
                                        title: item.title || 'Unknown',
                                        error: error.message
                                    });
                                }
                            }
                            
                            // Verify counts match
                            const expectedCount = response.data.cosyflows.length;
                            console.log(`[Cosyflows] Summary: Stored/Updated ${successCount} out of ${expectedCount} cosyflows`);
                            
                            if (failedItems.length > 0) {
                                console.log(`[Cosyflows] Failed items:`, failedItems);
                            }
                            
                            // Check final count in database
                            const finalCount = await db.cosyflows.count();
                            console.log(`[Cosyflows] Total cosyflows now in database: ${finalCount}`);
                            
                            // Verify all models were saved
                            if (successCount === expectedCount) {
                                console.log(`[Cosyflows] ✅ All models saved successfully!`);
                                
                                // Store success status
                                await db.settings.put({ 
                                    key: 'cosyflowsSyncStatus', 
                                    value: {
                                        complete: true,
                                        expectedCount,
                                        savedCount: successCount,
                                        lastSync: Date.now(),
                                        failedItems: []
                                    }
                                });
                            } else {
                                console.warn(`[Cosyflows] ⚠️ Mismatch: Expected ${expectedCount} models, but only saved ${successCount}`);
                                console.warn(`[Cosyflows] Missing models need to be saved on next app launch`);
                                
                                // Store a flag to indicate incomplete sync
                                await db.settings.put({ 
                                    key: 'cosyflowsSyncStatus', 
                                    value: {
                                        complete: false,
                                        expectedCount,
                                        savedCount: successCount,
                                        lastSync: Date.now(),
                                        failedItems
                                    }
                                });
                            }
                        } else {
                            console.log("[Cosyflows] API response missing cosyflows data");
                        }
                    } else {
                        console.log("[Cosyflows] Cosyflows already loaded, checking sync status...");
                        
                        // MINIMUM COUNT CHECK: Force sync if we have suspiciously few models
                        const MINIMUM_EXPECTED_MODELS = 50; // Reasonable minimum based on API having 86+ models
                        if (existingCount < MINIMUM_EXPECTED_MODELS) {
                            console.warn(`[Cosyflows] ⚠️ Only ${existingCount} models found, which is less than minimum expected ${MINIMUM_EXPECTED_MODELS}`);
                            console.log("[Cosyflows] Clearing sync status and forcing re-sync...");
                            
                            // Clear any existing sync status that might be blocking sync
                            await db.settings.delete('cosyflowsSyncStatus');
                            console.log("[Cosyflows] Sync status cleared");
                        }
                        
                        // Check if there was a previous sync status
                        const syncStatus = await db.settings.get('cosyflowsSyncStatus');
                        console.log("[Cosyflows] Current sync status:", syncStatus?.value || "No sync status found");
                        
                        // If no sync status exists OR sync is incomplete OR count is too low, we need to verify with API
                        if (!syncStatus?.value || !syncStatus.value.complete || existingCount < MINIMUM_EXPECTED_MODELS) {
                            if (!syncStatus?.value) {
                                console.log("[Cosyflows] No sync status found, verifying with API...");
                            } else if (!syncStatus.value.complete) {
                                console.log(`[Cosyflows] Previous sync was incomplete. Expected: ${syncStatus.value.expectedCount}, Saved: ${syncStatus.value.savedCount}`);
                            } else if (existingCount < MINIMUM_EXPECTED_MODELS) {
                                console.log(`[Cosyflows] Sync marked complete but only have ${existingCount} models (minimum expected: ${MINIMUM_EXPECTED_MODELS})`);
                            }
                            console.log("[Cosyflows] Attempting to fetch and save missing models...");
                            
                            // Fetch fresh data to compare
                            const axios = (await import('axios')).default;
                            const apiBase = await getApiBaseUrl();
                            const response = await axios.get(`${apiBase}/api/cosyflows/query`, {
                                headers: {
                                    "Content-Type": "application/json",
                                    "Authorization": `Bearer ${getAuthorizationToken()}`
                                }
                            });
                            
                            if (response.data && response.data.cosyflows) {
                                const apiCosyflows = response.data.cosyflows;
                                const existingCosyflows = await db.cosyflows.toArray();
                                
                                // Create a map of existing titles for quick lookup
                                const existingTitles = new Set(
                                    existingCosyflows
                                        .filter(cf => cf.data && cf.data.title)
                                        .map(cf => cf.data.title)
                                );
                                
                                console.log(`[Cosyflows] Found ${existingTitles.size} models in database`);
                                console.log(`[Cosyflows] API returned ${apiCosyflows.length} models`);
                                
                                // Find missing models
                                const missingModels = [];
                                for (const item of apiCosyflows) {
                                    try {
                                        const cosyflowData = JSON.parse(item.cosyflow);
                                        if (!existingTitles.has(cosyflowData.title)) {
                                            missingModels.push({ item, cosyflowData });
                                        }
                                    } catch (error) {
                                        console.error(`[Cosyflows] Error parsing cosyflow during sync check:`, error);
                                    }
                                }
                                
                                if (missingModels.length > 0) {
                                    console.log(`[Cosyflows] Found ${missingModels.length} missing models, saving them now...`);
                                    
                                    let syncSuccessCount = 0;
                                    for (const { item, cosyflowData } of missingModels) {
                                        try {
                                            await db.cosyflows.add({
                                                data: cosyflowData,
                                                timestamp: Date.now()
                                            });
                                            syncSuccessCount++;
                                            console.log(`[Cosyflows] Saved missing model: ${cosyflowData.title}`);
                                        } catch (error) {
                                            console.error(`[Cosyflows] Error saving missing model ${cosyflowData.title}:`, error);
                                        }
                                    }
                                    
                                    console.log(`[Cosyflows] Sync complete: saved ${syncSuccessCount} out of ${missingModels.length} missing models`);
                                    
                                    // Update sync status
                                    const finalCount = await db.cosyflows.count();
                                    const isComplete = finalCount === apiCosyflows.length;
                                    
                                    await db.settings.put({
                                        key: 'cosyflowsSyncStatus',
                                        value: {
                                            complete: isComplete,
                                            expectedCount: apiCosyflows.length,
                                            savedCount: finalCount,
                                            lastSync: Date.now(),
                                            failedItems: isComplete ? [] : (syncStatus?.value?.failedItems || [])
                                        }
                                    });
                                    
                                    if (isComplete) {
                                        console.log(`[Cosyflows] ✅ All models are now synced! Total: ${finalCount}`);
                                    } else {
                                        console.warn(`[Cosyflows] ⚠️ Still missing some models. Have ${finalCount} out of ${apiCosyflows.length}`);
                                    }
                                } else {
                                    console.log(`[Cosyflows] ✅ All models are already synced!`);
                                    
                                    // Update sync status to complete
                                    await db.settings.put({
                                        key: 'cosyflowsSyncStatus',
                                        value: {
                                            complete: true,
                                            expectedCount: apiCosyflows.length,
                                            savedCount: existingTitles.size,
                                            lastSync: Date.now(),
                                            failedItems: []
                                        }
                                    });
                                }
                            }
                        } else {
                            // No sync issues, just log the current state
                            const count = await db.cosyflows.count();
                            console.log(`[Cosyflows] ✅ Database has ${count} cosyflows, no sync needed`);
                        }
                    }
                    console.log("[Cosyflows] === COSYFLOWS SYNC CHECK COMPLETE ===");
                    } catch (cosyflowError) {
                        console.error("Error fetching/storing cosyflows:", cosyflowError);
                        console.log("[Cosyflows] === COSYFLOWS SYNC CHECK FAILED ===");
                    }
                } else {
                    console.log("[Cosyflows] Offline mode detected - skipping cosyflows sync to prevent app crashes");
                }
            } catch (error) {
                console.error("Error during recommended settings initialization:", error);
            }
        };
        
        setupRecommendedSettings();
        // updateAllModels();
    }, []);

    ipcRenderer.send("init-ini")

    electronBridge.log("React - Bringing in page")

    console.log(window.location.pathname);

    // Show popup if needed, otherwise the app
    if (showPopup) return popup;
    
    return <RouterProvider router={router} />;
}

export default App;
